// Lecture framework API — live multimodal lookups + segmentation + the marking
// user_response shape. Mirrors backend app/api/v1/lecture.py + lecture_marking.py.
import { api, API_BASE_URL, API_PREFIX, ApiError } from "./client";
import { tokenStore } from "./token-store";

// ── Source-PDF upload (POST /lecture/source-pdf) ─────────────────────────────
// Multipart, so this uses fetch directly (like files.ts). Returns the durable
// object-storage KEY to stash in start_inputs.source_pdf_key — the produced set
// then carries a downloadable + viewable source in the Library.
export async function uploadSourcePdf(file: File): Promise<{ key: string }> {
  if (file.size === 0) {
    throw new ApiError(422, "That PDF is empty — try selecting it again.");
  }
  const form = new FormData();
  form.append("file", file, file.name);
  const headers: Record<string, string> = { Accept: "application/json" };
  const token = tokenStore.access;
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE_URL}${API_PREFIX}/lecture/source-pdf`, {
    method: "POST",
    headers,
    body: form,
  });
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const data = await res.json();
      if (typeof data?.detail === "string") message = data.detail;
    } catch {
      /* keep generic */
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as { key: string };
}

// ── Live explain (POST /lecture/explain) ─────────────────────────────────────

export type LookupKind =
  | "definition"
  | "explain_equation"
  | "background_theory"
  | "variables"
  | "figure"
  | "general"
  | "intuition"
  | "example"
  | "why_matters"
  | "plot"
  | "question";

export interface ExplainResponse {
  kind: string;
  explanation: string;
  model: string;
  /** Per-call USD cost of this interactive look-up (null when unpriced). */
  cost_usd?: number | null;
}

export function explain(params: {
  selectedText?: string;
  context?: string | null;
  kind: LookupKind | string;
  language: string;
  question?: string | null;
  /** base64 of a snipped page region → answered by a vision model. */
  imageB64?: string | null;
  imageMime?: string;
  /** Server doc/run/file id → the backend attributes this call's cost to it. */
  documentRef?: string;
  /** Optional per-lookup model pick (the popover's model dropdown). Both must be
   *  set for the backend to honour it; else it uses the default fast model. */
  provider?: string | null;
  model?: string | null;
}): Promise<ExplainResponse> {
  return api.post<ExplainResponse>("/lecture/explain", {
    selected_text: params.selectedText ?? "",
    context: params.context ?? null,
    kind: params.kind,
    language: params.language,
    question: params.question ?? null,
    image_b64: params.imageB64 ?? null,
    image_mime: params.imageMime ?? "image/png",
    ...(params.documentRef ? { document_ref: params.documentRef } : {}),
    provider: params.provider ?? null,
    model: params.model ?? null,
  });
}

// ── Deep specialist card (POST /lecture/explain-card) ────────────────────────
// A field specialist authors a structured card (definition / explanation /
// intuition / probing question) that teaches the concept beyond the text —
// replaces the brief /explain blurb for marked selections.

export interface SpecialistCard {
  definition: string;
  explanation: string;
  intuition: string;
  question: string;
  answer: string;
  model?: string;
  /** Per-call USD cost of authoring this specialist card (null when unpriced). */
  cost_usd?: number | null;
}

export function explainCard(params: {
  selectedText?: string;
  context?: string | null;
  language: string;
  domain?: string | null;
  fieldPath?: string | null;
  /** Server doc/run/file id → the backend attributes this call's cost to it. */
  documentRef?: string;
}): Promise<SpecialistCard> {
  return api.post<SpecialistCard>("/lecture/explain-card", {
    selected_text: params.selectedText ?? "",
    context: params.context ?? null,
    language: params.language,
    domain: params.domain ?? null,
    field_path: params.fieldPath ?? null,
    ...(params.documentRef ? { document_ref: params.documentRef } : {}),
  });
}

// ── Plot a function (POST /lecture/plot) ─────────────────────────────────────
// On-demand sandboxed "plot agent": the backend has a lite LLM write matplotlib
// code, runs it in the isolated edupause-sandbox container, uploads the PNG, and
// returns its URL. The web client keeps the result as a lookup so the chart
// flows into the produced study set.

export interface PlotResponse {
  image_url: string;
  image_key: string;
  caption: string;
  model: string;
  /** Per-call USD cost of the plot agent run (null when unpriced). */
  cost_usd?: number | null;
}

export function plot(params: {
  selectedText?: string;
  context?: string | null;
  language: string;
  /** base64 of a snipped page region → the agent reads the equation from it. */
  imageB64?: string | null;
  imageMime?: string;
  /** Server doc/run/file id → the backend attributes this call's cost to it. */
  documentRef?: string;
}): Promise<PlotResponse> {
  return api.post<PlotResponse>("/lecture/plot", {
    selected_text: params.selectedText ?? "",
    context: params.context ?? null,
    language: params.language,
    image_b64: params.imageB64 ?? null,
    image_mime: params.imageMime ?? "image/png",
    ...(params.documentRef ? { document_ref: params.documentRef } : {}),
  });
}

// ── Segmentation (POST /lecture/segment) ─────────────────────────────────────

export interface SegmentSection {
  id: string;
  heading: string;
  summary: string;
  char_start: number;
  char_end: number;
}

export interface SegmentResponse {
  sections: SegmentSection[];
}

export function segment(params: {
  sourceText: string;
  language: string;
  maxSections?: number;
  /** Server doc/run/file id → the backend attributes this call's cost to it. */
  documentRef?: string;
}): Promise<SegmentResponse> {
  return api.post<SegmentResponse>("/lecture/segment", {
    source_text: params.sourceText,
    language: params.language,
    max_sections: params.maxSections ?? 18,
    ...(params.documentRef ? { document_ref: params.documentRef } : {}),
  });
}

// ── Understand (POST /lecture/understand) ────────────────────────────────────
// One-time document analysis. The marking workspace extracts the PDF text and
// calls this once on load; the compact bullets ground both live lookups
// (passed as `context`) and generation image-questions.

export interface UnderstandResponse {
  summary: string;
  topics: string[];
  learnings: string[];
}

export function understand(params: {
  sourceText: string;
  language: string;
  title?: string;
  /** Rendered page images (base64, no data: prefix) — the fallback for a
   *  scanned/handwritten PDF with no extractable text. */
  images?: { b64: string; mime: string }[];
  /** Server doc/run/file id → the backend attributes this call's cost to it. */
  documentRef?: string;
}): Promise<UnderstandResponse> {
  return api.post<UnderstandResponse>("/lecture/understand", {
    source_text: params.sourceText,
    language: params.language,
    title: params.title ?? "",
    images: params.images ?? [],
    ...(params.documentRef ? { document_ref: params.documentRef } : {}),
  });
}

// ── Marking usage summary (GET /marking-usage/summary) ───────────────────────
// The accumulated USD cost of the interactive marking look-ups (explain / card /
// plot / understand / segment), scoped to the current user. With a document_ref
// the total is scoped to that document; without it, the user's overall total.

export interface MarkingUsageSummary {
  total_cost_usd: number;
  request_count: number;
  by_kind: { kind: string; count: number; cost_usd: number }[];
}

export function getMarkingUsageSummary(documentRef?: string) {
  return api.get<MarkingUsageSummary>(
    "/marking-usage/summary" +
      (documentRef ? `?document_ref=${encodeURIComponent(documentRef)}` : ""),
  );
}

// ── Marking user_response (mirrors backend MarkingUserResponse) ───────────────

export type Depth = "from_scratch" | "recap_briefly" | "already_know";

export interface DeepMark {
  id: string;
  selected_text: string;
  label?: string | null;
  depth: Depth;
  surrounding_context?: string | null;
  section_ref?: string | null;
  image_b64?: string | null;
  image_mime?: string;
  color?: string | null;
  page?: number | null;
  /** Per-section generation toggles (preset keys + custom strings). */
  directives?: string[];
}

/** Preset generation toggles shown per deep mark. */
export const DIRECTIVE_PRESETS: { key: string; label: string }[] = [
  { key: "derive_equations", label: "Derive equations" },
  { key: "drill_memory", label: "Drill memory" },
  { key: "worked_examples", label: "Worked examples" },
  { key: "deep_understanding", label: "Deep understanding" },
  { key: "quick_recap", label: "Quick recap" },
  { key: "real_world", label: "Real-world" },
];

export interface Lookup {
  id: string;
  kind: string;
  selected_text: string;
  explanation: string;
  /** Specialist deep-card facets (POST /lecture/explain-card). Optional so
   *  legacy explanation-only lookups still serialize. */
  definition?: string;
  intuition?: string;
  question?: string;
  answer?: string;
  section_ref?: string | null;
  image_b64?: string | null;
  image_mime?: string;
  color?: string | null;
  page?: number | null;
}

/** A plain highlight — a soft, visual-only signal that weakly nudges generation
 *  toward the flagged passage (no forced topic/questions; text-only). */
export interface HighlightSignal {
  id?: string;
  selected_text: string;
  page?: number;
  color?: string;
}

export interface MarkingUserResponse {
  deep_marks: DeepMark[];
  lookups: Lookup[];
  /** Plain highlights — soft, low-priority emphasis (see HighlightSignal). */
  highlights?: HighlightSignal[];
  /** Compact one-time document understanding (summary + key learnings) so
   *  generation image-questions are grounded in the whole document. */
  document_context?: string;
}

// ── Marking step output (mirrors backend MarkingOutput) ──────────────────────

export interface MarkingSection {
  label: string;
  slug: string;
  summary?: string;
  char_start?: number | null;
  char_end?: number | null;
}

export interface MarkingOutput {
  source_text: string;
  sections: MarkingSection[];
  language: string;
  title?: string | null;
}

export const DEPTH_LABELS: Record<Depth, string> = {
  from_scratch: "Full",
  recap_briefly: "Brief",
  already_know: "Just test",
};

// Heat scheme — one hue per depth, the single source of truth for both the menu
// buttons and the rendered page highlight. Full=amber, Brief=blue, Just test=green.
export const DEPTH_COLORS: Record<Depth, string> = {
  from_scratch: "#eab308",
  recap_briefly: "#3b82f6",
  already_know: "#22c55e",
};
